import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { faqs } from '@/mocks/faq';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEOHead
        title="关于我们 - 暖暖爱视频 | 品牌介绍与联系方式"
        description="了解暖暖爱视频的品牌故事、口碑声誉和联系方式。暖暖爱视频致力于为用户提供免费高清视频在线观看服务，无需注册、无需下载，随时随地畅享全集视频内容。"
        keywords="暖暖爱视频,关于暖暖爱,暖暖爱品牌,暖暖爱联系方式,免费视频平台"
        canonicalUrl="https://www.nuannuanai.com/about"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于暖暖爱视频',
            description: '了解暖暖爱视频的品牌故事、口碑声誉和联系方式。',
            about: {
              '@type': 'Organization',
              name: '暖暖爱视频',
              url: 'https://www.nuannuanai.com/',
              description: '免费高清视频在线观看平台',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.nuannuanai.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />

      <Navbar />
      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: '关于我们' },
        ]}
      />

      <main>
        {/* Hero */}
        <section className="py-16 md:py-24 bg-background-50 text-center">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <span className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-6 rounded-full bg-primary-100 text-primary-500">
              <i className="ri-heart-fill text-3xl md:text-4xl"></i>
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mb-5">
              关于暖暖爱视频
            </h1>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed max-w-2xl mx-auto">
              暖暖爱视频是一个致力于为用户提供免费高清视频内容的在线平台。我们坚信，优质的视频内容应该让每个人都能轻松获取，无需任何门槛。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                  品牌故事
                </h2>
                <div className="space-y-4">
                  <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                    暖暖爱视频诞生于对视频分享的热爱。我们的创始团队发现，很多优秀的视频内容因为平台限制或付费门槛，无法被更多人看到。于是，我们决定打造一个完全免费、无需注册的视频平台。
                  </p>
                  <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                    从最初的几十部视频到现在涵盖爱情剧、都市剧、古装剧、悬疑剧、美食、旅行、家居、亲子等丰富品类，暖暖爱视频始终坚持初心：让每个人都能轻松享受到高清流畅的视频内容。
                  </p>
                  <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                    我们的名字"暖暖爱"寄托了最朴素的愿景——用温暖的视频内容，传递爱与美好。无论是感人的爱情故事、精彩的生活记录，还是实用的知识分享，我们都希望用户在这里找到属于自己的那份温暖。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cozy%20creative%20workspace%20with%20team%20collaborating%2C%20soft%20natural%20lighting%2C%20modern%20minimalist%20office%2C%20warm%20wood%20tones%2C%20creative%20atmosphere%2C%20friendly%20team%20vibe%2C%20beautiful%20lifestyle%20photography&width=800&height=600&seq=about-brand-story&orientation=landscape"
                  alt="暖暖爱视频品牌故事"
                  title="暖暖爱视频品牌故事"
                  className="w-full object-cover rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
              {[
                { value: '200+', label: '视频内容' },
                { value: '8', label: '内容分类' },
                { value: '500万+', label: '月活跃用户' },
                { value: '98%', label: '用户好评率' },
              ].map((stat, i) => (
                <div key={i} className="p-5 md:p-6 bg-background-100 rounded-lg">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">
                    {stat.value}
                  </p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              我们的承诺
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: '完全免费',
                  desc: '无需注册、无需付费，所有视频永久免费观看',
                },
                {
                  icon: 'ri-4k-line',
                  title: '高清画质',
                  desc: '支持1080P高清播放，清晰流畅的观影体验',
                },
                {
                  icon: 'ri-smartphone-line',
                  title: '多端支持',
                  desc: '手机、平板、电脑全平台适配，随时随地观看',
                },
                {
                  icon: 'ri-refresh-line',
                  title: '持续更新',
                  desc: '每日更新最新内容，精彩内容不断档',
                },
              ].map((val, i) => (
                <div
                  key={i}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center"
                >
                  <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center mx-auto mb-4 rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${val.icon} text-xl md:text-2xl`}></i>
                  </span>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                    {val.title}
                  </h3>
                  <p className="text-xs md:text-sm text-foreground-400 leading-relaxed">
                    {val.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {[
                {
                  name: '张女士',
                  role: '忠实用户 · 使用2年',
                  comment: '暖暖爱视频真的太好用了！不需要注册就能直接看，画质还特别清晰。下班回家就想看两集暖暖爱，放松一下。',
                  rating: 5,
                },
                {
                  name: '李先生',
                  role: '家庭用户',
                  comment: '周末全家人一起在暖暖爱上看视频，内容丰富选择多，孩子喜欢看亲子内容，我和太太看爱情剧，各取所需。',
                  rating: 5,
                },
                {
                  name: '王同学',
                  role: '大学生用户',
                  comment: '寝室里大家都在用暖暖爱视频，免费又高清，关键是更新快，新剧第一时间就能看到。强烈推荐给同学们！',
                  rating: 5,
                },
              ].map((review, i) => (
                <div
                  key={i}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <span key={j} className="w-4 h-4 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                    "{review.comment}"
                  </p>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 text-sm font-bold">
                      {review.name[0]}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  联系方式
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-500">support@nuannuanai.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-customer-service-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">客服热线</p>
                      <p className="text-sm text-foreground-500">400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  快速反馈
                </h3>
                <p className="text-sm text-foreground-500 mb-5">
                  如果您有任何问题或建议，欢迎通过以下方式联系我们，我们将在24小时内回复。
                </p>
                <div className="flex items-center gap-3">
                  <a
                    href="mailto:support@nuannuanai.com"
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line mr-2"></i>
                    发送邮件
                  </a>
                  <a
                    href="tel:4008889999"
                    className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    拨打电话
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-10 text-center">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.slice(0, 5).map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                  >
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 pr-4">
                      {faq.question}
                    </h3>
                    <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFaq === index ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-lg text-foreground-400"></i>
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}